/**
 * 小红书官方 JS SDK 分享（https://agora.xiaohongshu.com/doc/js）
 */

const XHS_SDK_URLS = [
  'https://fe-static.xhscdn.com/biz-static/goten/xhs-1.0.1.js',
  process.env.BASE_URL + 'js/xhs-1.0.1.js',
  process.env.BASE_URL + 'js/xhs-1.1.0.js'
]

let sdkLoadPromise = null

export function detectXhsEnv() {
  const ua = String(navigator.userAgent || '')
  const isHarmonyOS = /HarmonyOS|OpenHarmony|ArkWeb/i.test(ua)
  const isWeChat = /MicroMessenger/i.test(ua)
  const isIOS = /iPhone|iPad|iPod/i.test(ua)
  const isAndroid = /Android/i.test(ua)
  const isMobile = isIOS || isAndroid || isHarmonyOS
  return { ua, isHarmonyOS, isWeChat, isIOS, isAndroid, isMobile }
}

function loadScript(url) {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = url
    script.async = true
    script.onload = () => resolve(url)
    script.onerror = () => reject(new Error('load failed: ' + url))
    document.head.appendChild(script)
  })
}

export function loadXhsSdk() {
  if (typeof window !== 'undefined' && window.xhs && typeof window.xhs.share === 'function') {
    return Promise.resolve(window.xhs)
  }
  if (sdkLoadPromise) {
    return sdkLoadPromise
  }
  sdkLoadPromise = (async () => {
    let lastError = null
    for (const url of XHS_SDK_URLS) {
      try {
        await loadScript(url)
        if (window.xhs && typeof window.xhs.share === 'function') {
          return window.xhs
        }
      } catch (e) {
        lastError = e
      }
    }
    throw lastError || new Error('小红书 SDK 加载失败')
  })()
  return sdkLoadPromise
}

/**
 * 官方 SDK 只有 fail 回调；成功时会直接跳转 App，不会触发 success。
 */
export function shareViaOfficialSdk(shareConfig) {
  const shareInfo = shareConfig && shareConfig.shareInfo
  const verifyConfig = shareConfig && shareConfig.verifyConfig
  if (!shareInfo || !verifyConfig) {
    return Promise.reject(new Error('缺少小红书分享参数'))
  }
  return loadXhsSdk().then(xhs => new Promise((resolve, reject) => {
    let settled = false
    const finishOk = () => {
      if (settled) return
      settled = true
      resolve({ ok: true })
    }
    const finishFail = (err) => {
      if (settled) return
      settled = true
      reject(err || new Error('小红书分享失败'))
    }
    const timer = setTimeout(finishOk, 1200)
    const onHide = () => {
      if (document.hidden) {
        clearTimeout(timer)
        finishOk()
      }
    }
    document.addEventListener('visibilitychange', onHide)

    try {
      xhs.share({
        shareInfo,
        verifyConfig,
        fail: (err) => {
          clearTimeout(timer)
          document.removeEventListener('visibilitychange', onHide)
          finishFail(err)
        }
      })
    } catch (err) {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onHide)
      finishFail(err)
    }
  }))
}

export function mapShareDisableReason(shareConfig) {
  const reason = shareConfig && shareConfig.reason
  const message = shareConfig && shareConfig.message
  if (reason === 'xhs_share_not_configured') {
    return '未配置小红书分享 AppKey/AppSecret'
  }
  if (reason === 'no_public_images') {
    return '活动图片不是公网 HTTPS 地址，无法带入小红书'
  }
  if (reason === 'xhs_share_sign_failed') {
    if (message && message.indexOf('未注册') >= 0) {
      return '分享应用未在小红书分享开放平台注册（agora.xiaohongshu.com），请联系管理员完成「快速接入」'
    }
    return message || '小红书分享鉴权失败'
  }
  return message || reason || '小红书官方分享不可用'
}

export function getFallbackLaunchPlan(env) {
  if (env.isWeChat) {
    return {
      mode: 'wechat_manual',
      tip: '微信内无法直接跳转。请点右上角「…」→ 在浏览器打开后再发布。',
      successMsg: '文案已复制，请用系统浏览器打开本页后再试'
    }
  }
  if (env.isHarmonyOS) {
    return {
      mode: 'harmony_manual',
      scheme: 'xhsdiscover://post',
      tip: '官方 SDK 不可用时：文案已复制，请打开小红书发笔记页粘贴。',
      successMsg: '文案已复制，请在小红书内手动发笔记'
    }
  }
  return {
    mode: 'scheme',
    scheme: 'xhsdiscover://post',
    tip: '官方 SDK 不可用时，将复制文案并尝试打开小红书发笔记页。',
    successMsg: '文案已复制，请在小红书发布页粘贴'
  }
}

export function openSchemeUrl(url) {
  if (!url) return
  try {
    window.location.href = url
  } catch (e) {
    // ignore
  }
}

export function fallbackOpenXhs(env, schemeUrl) {
  const plan = getFallbackLaunchPlan(env)
  openSchemeUrl(schemeUrl || plan.scheme)
  return plan
}

export function copyText(text) {
  const value = String(text || '')
  if (!value) return Promise.resolve(false)
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(value).then(() => true).catch(() => copyTextFallback(value))
  }
  return Promise.resolve(copyTextFallback(value))
}

function copyTextFallback(text) {
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    ta.setSelectionRange(0, text.length)
    const ok = document.execCommand('copy')
    document.body.removeChild(ta)
    return ok
  } catch (e) {
    return false
  }
}

export function formatXhsShareError(err) {
  if (!err) return '小红书分享失败'
  if (typeof err === 'string') return err
  if (err.message) return err.message
  if (err.msg) return err.msg
  try {
    return JSON.stringify(err)
  } catch (e) {
    return '小红书分享失败'
  }
}
